import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { GenericFormSection } from "models/propTypes/genericFormProps";
import { TodoFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { useTodoService } from "serviceHooks";
//#endregion
import { EnumHelper, QueryStateHelper, UtilsHelper } from "utils/helpers";

function useTodoReportFilterDefinition(includeDeadlineTypes?: boolean) {
  const todoService = useTodoService();
  const [selectedSubtypes, setSelectedSubtypes] = React.useState<SelectionItem[]>();

  const dedalineTypes = todoService.getDefaultDeadlineTypes();

  const groupTypes = EnumHelper.getNumericItemValues(WintSuperkollAPIModel.TodoGroup).map((group) => ({
    id: group,
    value: todoService.getGroupTypeText(group),
  }));

  const groupedSubTypes = EnumHelper.getNumericItemValues(WintSuperkollAPIModel.TodoSubType).reduce(
    (uniqueSubTypes: SelectionItem[], subType) => {
      const currentSubTypeName = todoService.getTodoCardSubTypeName(subType);

      if (UtilsHelper.isEmpty(currentSubTypeName.trim())) return uniqueSubTypes;

      const existingSubType = uniqueSubTypes.find((subType) => subType.value === currentSubTypeName);

      if (existingSubType) {
        existingSubType.id = [existingSubType.id, subType.toString()].join(",");
      } else {
        uniqueSubTypes.push({
          value: currentSubTypeName,
          id: subType,
        });
      }

      return uniqueSubTypes;
    },
    []
  );

  const urgencyTypes = EnumHelper.getNumericItemValues(WintSuperkollAPIModel.TodoUrgency).map((urgency) => ({
    id: urgency,
    value: todoService.getUrgencyTypeText(urgency),
  }));

  const todosGroupSummary = useSelector((store: AppState) => store.todos.todoSummary?.todosGroupSummary);

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModel.TodoFilter
  ): Promise<TodoFormTypes.Filter> => {
    const reportFilter: TodoFormTypes.Filter = {
      groups: filter.groups?.map((groupId) => ({
        id: groupId,
        value: todoService.getGroupTypeText(groupId),
      })),
      subTypes: groupedSubTypes.filter((gs) =>
        `${gs.id}`.split(",").some((s) => filter.subTypes?.some((f) => f.toString() === s))
      ),
      urgency: filter.urgency?.map((urgencyId) => ({
        id: urgencyId,
        value: todoService.getUrgencyTypeText(urgencyId),
      })),
      deadlineTypes: filter.deadlineTypes?.map((deadlineTypeId) => ({
        id: deadlineTypeId,
        value: todoService.getDeadlineTypeName(deadlineTypeId),
      })),
      snoozedOnly: filter.isSnoozed,
    };

    return reportFilter;
  };

  const getSelectableTodoGroupsInFilter = () => {
    if (!todosGroupSummary?.length) return [];

    const groupIdsWithDuplicates = todosGroupSummary.map((summaryItem) => summaryItem.group);
    const uniqueGroupIds = Array.from(new Set(groupIdsWithDuplicates));
    return uniqueGroupIds.map(
      (uniqueGroupId) =>
        ({
          id: uniqueGroupId,
          value: todoService.getGroupTypeText(uniqueGroupId),
        } as SelectionItem)
    );
  };

  const getSelectableTodoSubTypesInFilter = (groups?: SelectionItem[]) => {
    if (!todosGroupSummary?.length) return [];

    const includedSubTypes = todosGroupSummary
      .filter((t) => !groups?.length || groups.some((g) => g.id === t.group))
      .map((todoItem) => todoItem.subType);

    return groupedSubTypes.filter((subType) =>
      typeof subType.id === "string"
        ? includedSubTypes.some((t) => subType.id.toString().split(",").indexOf(t.toString()) >= 0)
        : includedSubTypes.includes(+subType.id)
    );
  };

  const setSelectableTodoSubTypesInFilter = (name?: string, groups?: SelectionItem[]) => {
    setSelectedSubtypes(getSelectableTodoSubTypesInFilter(groups));
  };

  const filterFormFields: GenericFormSection<TodoFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.Filter,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        groups: {
          label: TranslationKeys.Common.Type,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            items: getSelectableTodoGroupsInFilter(),
          },
          onChangeHandler: setSelectableTodoSubTypesInFilter,
          enableOnChangeAlways: true,
        },
        subTypes: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            items: selectedSubtypes ?? getSelectableTodoSubTypesInFilter(),
          },
        },
        urgency: {
          label: TranslationKeys.Common.Urgency,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: urgencyTypes,
          },
        },
        deadlineTypes: {
          label: TranslationKeys.Common.Deadline,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          hidden: !includeDeadlineTypes,
          extraProps: {
            items: dedalineTypes,
          },
        },
        snoozedOnly: {
          label: TranslationKeys.Common.Snoozed,
          type: FormEnums.FormFieldType.BOOLEANSELECTION,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<TodoFormTypes.Filter> = {
    snoozedOnly: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.Todo.Filter.SnoozedOnly),
    urgency: QueryStateHelper.selectionItemsQueryEncoder(FormFieldConstants.Todo.Filter.Urgency, urgencyTypes),
    groups: QueryStateHelper.selectionItemsQueryEncoder(FormFieldConstants.Todo.Filter.Groups, groupTypes),
    subTypes: QueryStateHelper.selectionItemsQueryEncoder(FormFieldConstants.Todo.Filter.SubTypes, groupedSubTypes),
    deadlineTypes: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Todo.Filter.DeadlineTypes,
      dedalineTypes
    ),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useTodoReportFilterDefinition;
